import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, PauseCircle } from "@phosphor-icons/react";

const FALLBACK_TYPES = [
  { key: "innova_sign", label: "Innova-Sign", section: "innova_sign" },
  { key: "fixed_line", label: "Ligne fixe", section: "local_access" },
  { key: "ip_line", label: "Ligne IP", section: "ip_lines" },
  { key: "internet", label: "Internet", section: "internet" },
];

export const newPause = () => ({ from_date: "", to_date: "", scope: "innova_sign", note: "" });

export function PauseRows({ pauses, setPauses, types = FALLBACK_TYPES }) {
  const set = (i, k, v) => setPauses(pauses.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  return (
    <div className="space-y-2">
      {pauses.length > 0 && (
        <div className="hidden md:grid grid-cols-[150px_150px_200px_1fr_36px] gap-2 px-1 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
          <span>Du</span><span>Au</span><span>Services visés</span><span>Remarque</span><span></span>
        </div>
      )}
      {pauses.map((p, i) => (
        <div key={i} className="grid grid-cols-2 md:grid-cols-[150px_150px_200px_1fr_36px] gap-2 items-center" data-testid={`pause-row-${i}`}>
          <Input data-testid={`pause-from-${i}`} type="date" className="rounded-sm" value={p.from_date} onChange={(e) => set(i, "from_date", e.target.value)} />
          <Input data-testid={`pause-to-${i}`} type="date" className="rounded-sm" value={p.to_date} onChange={(e) => set(i, "to_date", e.target.value)} />
          <Select value={p.scope} onValueChange={(v) => set(i, "scope", v)}>
            <SelectTrigger data-testid={`pause-scope-${i}`} className="rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {types.filter((t) => t.section).map((t) => <SelectItem key={t.key} value={t.key}>Services {t.label}</SelectItem>)}
              <SelectItem value="all">Tous les services (pause complète)</SelectItem>
            </SelectContent>
          </Select>
          <Input data-testid={`pause-note-${i}`} className="rounded-sm" placeholder="Ex. fermeture saisonnière" value={p.note || ""} onChange={(e) => set(i, "note", e.target.value)} />
          <Button variant="ghost" size="icon" className="h-9 w-9 hover:text-destructive" onClick={() => setPauses(pauses.filter((_, idx) => idx !== i))}><Trash size={16} /></Button>
        </div>
      ))}
      <Button data-testid="add-pause-btn" variant="outline" size="sm" className="rounded-sm" onClick={() => setPauses([...pauses, newPause()])}><Plus size={14} className="mr-1" /> Ajouter une période de pause</Button>
      <p className="text-xs text-muted-foreground">Les frais mensuels des services visés ne sont pas facturés pour les échéances comprises dans ces périodes (ex. Innova-Sign fermé l'hiver). La facturation reprend automatiquement après.</p>
    </div>
  );
}

export default function PausesDialog({ contract, onOpenChange, onSaved }) {
  const [pauses, setPauses] = useState([]);
  const [types, setTypes] = useState(FALLBACK_TYPES);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!contract) return;
    setPauses((contract.pauses || []).map((p) => ({ ...p })));
    api.get("/contracts/options").then((r) => setTypes(r.data.service_types)).catch(() => {});
  }, [contract]);

  const save = async () => {
    if (pauses.some((p) => !p.from_date || !p.to_date)) { toast.error("Chaque période doit avoir une date de début et de fin."); return; }
    setSaving(true);
    try { await api.put(`/contracts/${contract.id}/pauses`, { pauses }); toast.success("Périodes de pause enregistrées"); onOpenChange(false); onSaved?.(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!contract} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-3xl" data-testid="pauses-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><PauseCircle size={20} /> Périodes de pause — contrat {contract?.contract_number}</DialogTitle>
          <DialogDescription>Suspension saisonnière : indiquez les périodes pendant lesquelles les services visés ne sont pas facturés. Elles sont imprimées dans la Solution détaillée du contrat.</DialogDescription>
        </DialogHeader>
        <PauseRows pauses={pauses} setPauses={setPauses} types={types} />
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button data-testid="save-pauses-btn" className="rounded-sm" onClick={save} disabled={saving}>{saving ? "Enregistrement…" : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
